import type { Cart } from '~~/types/commercetools';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import { isCheckoutPageSlug } from '~~/helpers/routes/getPageInformation';
import { getIsExtraGuaranteeOnlyCart } from '~~/helpers/cart/getIsExtraGuaranteeOnlyCart';
import { isQueryOnlyNavigation } from '~~/helpers/routes/isQueryOnlyNavigation';
import { useExpondoMiddleware } from '~/composables/useExpondoMiddleware';
import { Logger } from '~~/helpers/logger';
import { defineNuxtRouteMiddleware } from '#imports';

/**
 * Middleware that clears the cart if:
 * 1. All items in the cart are of EXTRA_GUARANTEE type
 * 2. User is navigating to a page other than checkout
 *
 * This prevents extra guarantee carts from persisting when users navigate away from checkout,
 * as extra guarantee carts are not compatible with normal product carts.
 */

export default defineNuxtRouteMiddleware(async (to, from) => {

  if (isCheckoutPageSlug(to.path)) {

    return;

  }

  // Changing filters, sorting or pagination does not leave the page.
  // The cart was already checked when the user opened this page, so there is no need to check it again.
  if (isQueryOnlyNavigation(to, from)) {

    return;

  }

  try {

    const { makeCommercetoolsRequest } = useExpondoMiddleware();

    const response = await makeCommercetoolsRequest<{ me?: { activeCart?: Cart | null } }>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.GET_ME,
    });

    const activeCart = response?.data?.me?.activeCart;

    if (!activeCart?.lineItems?.length) {

      return;

    }

    if (!getIsExtraGuaranteeOnlyCart(activeCart)) {

      return;

    }

    await makeCommercetoolsRequest({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.DELETE_CART,
      data: [{ id: activeCart.id, version: activeCart.version }],
    });

  } catch (error) {

    Logger.error('clear-extra-guarantee-cart middleware failed', error);

  }

});
